import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/Workspaces/Video/Objects/Object'
import { useVideoObjectUpdateMutation } from 'hooks/mutations/content'
import { videoObjectBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs'
import {
  videoObjectConfigRoute,
  videoObjectRoute,
  videoObjectWatchRoute,
} from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Routing'
import { videoObjectQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { videoobjectId: objectId } = videoObjectRoute.useParams()

  const videoObjectQuery = useQuery(videoObjectQueryKeys.detail({ videoId: objectId }))

  const { mutate, ...videoObjectUpdateMutation } = useVideoObjectUpdateMutation()

  const handleUpdatevideoObjectName = useCallback(
    (title: string, closeCallback: () => void) => {
      mutate(
        {
          videoId: objectId,
          videoObjectEdit: { name: title },
        },
        {
          onError: () => enqueueSnackbar(t('pages:Workspace.contentProduction')),
          onSettled: closeCallback,
        },
      )
    },
    [t, mutate, objectId, enqueueSnackbar],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={videoObjectQuery.data?.name ?? ''}
          isLoading={videoObjectQuery.isLoading}
          isSaving={videoObjectUpdateMutation.isPending}
          onSave={handleUpdatevideoObjectName}
          Actions={<ActionsMenu />}
          TabNavigation={
            <Tabs>
              <NavigationTabLink from={videoObjectRoute.to} to={videoObjectWatchRoute.to}>
                {t('pages:Workspace.watch')}
              </NavigationTabLink>
              <NavigationTabLink from={videoObjectRoute.to} to={videoObjectConfigRoute.to}>
                {t('pages:Workspace.config')}
              </NavigationTabLink>
              <NavigationTabLink from={videoObjectRoute.to} to={videoObjectBlobsOverviewRoute.to}>
                {t('entity:content.blob', { count: 2 })}
              </NavigationTabLink>
            </Tabs>
          }
        />
      }
    />
  )
}

export const Structure = memo(StructureRaw)
